'use client';

import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import ModalBase from '../../../../components/ui/ModalBase/ModalBase';
import { Format } from '../../../../lib/dateFns';
import type { VariantType } from '../../../../types/variant';
import { formatDateCommon } from '../../../../utils/formatters';
import type { CongestionLevel } from '../../api/getCongestion';
import styles from './CongestionModal.module.scss';
import raffleStyles from './CongestionModal.raffle.module.scss';

/** 混雑レベルに応じて表示するメッセージ */
const CONGESTION_MESSAGES: Record<CongestionLevel, string> = {
  low: '',
  moderate: '',
  high: 'ただいま混み合っています\n時間をおいてお試しください',
  full: 'ただいま混み合っています\n時間をおいてお試しください',
};

/** 混雑レベルに応じて点灯するインジケーターの数 */
const CONGESTION_INDICATOR_COUNT: Record<CongestionLevel, number> = {
  low: 1,
  moderate: 2,
  high: 3,
  full: 4,
};

/** インジケーターの総数 */
const INDICATOR_TOTAL = 4;
/** インジケーター1つあたりのアニメーション間隔（ミリ秒） */
const INDICATOR_SEGMENT_DURATION = 460;
/** 全インジケーター点灯時の一時停止倍率 */
const INDICATOR_MAX_PAUSE_MULTIPLIER = 1;


export type CongestionModalProps = {
  /** モーダルの表示状態 */
  isOpen: boolean;
  /** モーダルのタイトル */
  title: string;
  /** 混雑レベル（low/moderate/high/full） */
  congestionLevel: CongestionLevel;
  /** リトライ可能になるまでの秒数 */
  retryAfter: number;
  /** リトライボタン押下時のコールバック */
  onRetry: () => void;
  /** キャンセルボタン押下時のコールバック */
  onCancel: () => void;
  /** イベント終了日時（ISO8601形式） */
  eventEndAt?: string;
  /** コイン購入処理かどうか */
  isCoinPurchase?: boolean;
  /** 先着順である旨のメッセージを表示するかどうか */
  isFirstComeFirstServed?: boolean;
  /** スタイルのバリアント（オプション） */
  variant?: VariantType;
};

/**
 * 混雑状況モーダルコンポーネント
 *
 * くじ引き時にサーバーが混雑している場合に表示されるモーダル。
 * 混雑レベルをインジケーターで視覚的に表示し、
 * 一定時間後にリトライできるボタンを提供する。
 */
function CongestionModal({
  isOpen,
  title,
  congestionLevel,
  retryAfter,
  onRetry,
  onCancel,
  eventEndAt,
  isCoinPurchase = false,
  isFirstComeFirstServed = false,
  variant = 'default',
}: CongestionModalProps) {
  const currentStyles = variant === 'raffle' ? raffleStyles : styles;

  // リトライボタンが有効になるまでのカウントダウン秒数
  const [countdown, setCountdown] = useState(retryAfter);
  // 現在アニメーション中のインジケーターのインデックス
  const [activeIndicatorIndex, setActiveIndicatorIndex] = useState(0);

  // カウントダウンタイマーの管理
  useEffect(() => {
    if (!isOpen) {
      setCountdown(retryAfter);
      return;
    }

    setCountdown(retryAfter);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, retryAfter]);

  // カウントダウンが0になるまでリトライを無効化
  const handleRetry = useCallback(() => {
    if (countdown > 0) return;
    onRetry();
  }, [countdown, onRetry]);

  // 現在の混雑レベルに応じた設定値を取得
  const activeCount = CONGESTION_INDICATOR_COUNT[congestionLevel];
  const message = CONGESTION_MESSAGES[congestionLevel];

  // インジケーターのアニメーション制御
  // 点灯数を順番に増やし、全点灯後に一度消灯するループアニメーション
  useEffect(() => {
    if (!isOpen) {
      setActiveIndicatorIndex(0);
      return;
    }

    const availableIndicators = Math.max(activeCount, 0);
    if (availableIndicators === 0) {
      setActiveIndicatorIndex(0);
      return;
    }

    // アニメーションのシーケンスを生成（例: [1, 2, 3, 0] → 1個点灯 → 2個点灯 → 3個点灯 → 全消灯）
    const sequence = Array.from({ length: availableIndicators }, (_, index) => index + 1);
    sequence.push(0); // 0のタイミングで一度全消灯

    let sequenceIndex = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // 再帰的にアニメーションを実行する関数
    const tick = () => {
      const currentValue = sequence[sequenceIndex];
      setActiveIndicatorIndex(currentValue);

      // 全点灯時は一時停止倍率を適用
      const isMaxValue = currentValue === availableIndicators;
      const duration =
        INDICATOR_SEGMENT_DURATION * (isMaxValue ? INDICATOR_MAX_PAUSE_MULTIPLIER : 1);

      // 次のシーケンスへ遷移
      timeoutId = setTimeout(() => {
        sequenceIndex = (sequenceIndex + 1) % sequence.length;
        tick();
      }, duration);
    };

    tick();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeCount, isOpen]);

  const eventEndAtText = eventEndAt ? formatDateCommon(eventEndAt, Format.dateTimeLotteryJa) : null;

  return (
    <ModalBase isOpen={isOpen} onClose={onCancel} showCloseButton={false}>
      <div className={currentStyles.container}>
        <h2 className={currentStyles.title}>{title}</h2>

        <p className={currentStyles.label}>混雑状況</p>

        <div className={currentStyles.indicators}>
          {Array.from({ length: INDICATOR_TOTAL }, (_, mapIndex) => {
            const index = mapIndex + 1;
            const isEnabled = index <= activeCount;
            const isActive = isEnabled && activeIndicatorIndex === index;

            return (
              <div
                key={index}
                className={`${currentStyles.indicator} ${isEnabled ? currentStyles.indicatorEnabled : ''} ${
                  isActive ? currentStyles.indicatorActive : ''
                }`}
              />
            );
          })}
        </div>
        {message && (
          <p className={currentStyles.message}>
            {/** 改行コードを<br />に変換する */}
            {message.split('\n').map((line, idx, arr) => (
              // Lintエラー回避のためヒント文を追加する。
              // biome-ignore lint/suspicious/noArrayIndexKey: 配列のインデックスをキーとして使用
              <React.Fragment key={idx}>
                {line}
                {idx  < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        )}
        {isFirstComeFirstServed && (
          <p className={currentStyles.firstComeFirstServedMessage}>
            一部の景品は在庫数に限りがあるため、先着順でのご提供となります。在庫がなくなり次第、該当景品の販売は終了となりますので、あらかじめご了承ください。
          </p>
        )}
        {isCoinPurchase && (
          <p className={currentStyles.subMessage}>
            お支払い画面の準備をしています。
            <br />
            重複して決済されることはありませんので、
            <br />
            安心して次へお進みください。
          </p>
        )}
        {eventEndAtText && (
          <p className={currentStyles.subMessage}>
            くじは{eventEndAtText}まで開催しております。
            <br />
            時間をおいてお試しください。
          </p>
        )}

        <div className={currentStyles.buttons}>
          <ActionButton color='primary' onClick={handleRetry} disabled={countdown > 0}>
            {countdown > 0 ? `もう一度試す(${countdown}秒)` : 'もう一度試す'}
          </ActionButton>
          <ActionButton outlined color='white' bgColor='brownishgray' onClick={onCancel}>
            キャンセル
          </ActionButton>
        </div>
      </div>
    </ModalBase>
  );
}

export { CongestionModal };
